import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  "Store Owner / Operator",
  "Facilities Manager",
  "Operations Director",
  "Property Manager",
  "Regional / District Manager",
  "Other",
];

const PROPERTIES = [
  "Grocery / Supermarket",
  "Independent Grocer",
  "Pharmacy / Drug Store",
  "Dollar / Discount Retail",
  "Home Improvement",
  "Healthcare / Medical",
  "Hospitality / Hotel",
  "Airport / Transit",
  "Other Commercial",
];

const REASSURANCE = [
  "No pricing discussed until we understand your specific needs and property scope.",
  "No commitment required to have a conversation.",
  "Discovery calls are focused and typically 20–30 minutes.",
  "Serving Northeast Ohio and expanding regionally — inquiries welcome from all markets.",
];

const initial = {
  first_name: "",
  last_name: "",
  business: "",
  role: "",
  property_type: "",
  email: "",
  phone: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.first_name ||
      !form.last_name ||
      !form.business ||
      !form.role ||
      !form.property_type ||
      !form.email
    ) {
      toast.error("Please fill the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm(initial);
      toast.success("Request received. We'll reach out within one business day.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 md:py-32 bg-[var(--xw-bg)] border-b border-[var(--xw-line)]"
    >
      <div className="xw-container grid grid-cols-12 gap-10 lg:gap-12">
        {/* Left */}
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono-xw text-[11px] tracking-[0.22em] uppercase text-[var(--xw-orange)]">
              § 05 — Get in Touch
            </span>
            <span className="h-px w-16 bg-[var(--xw-line)]" />
          </div>
          <h2
            data-testid="contact-headline"
            className="font-display font-black uppercase tracking-[-0.03em] leading-[0.9]"
            style={{ fontSize: "clamp(2rem, 6.5vw, 4rem)" }}
          >
            Let's Talk About <br />
            <span className="italic font-medium" style={{ color: "var(--xw-orange)" }}>
              Your Exterior.
            </span>
          </h2>
          <p className="mt-6 text-[15px] md:text-[16px] leading-relaxed text-[var(--xw-ink-soft)] max-w-[55ch]">
            We work with store owners, facilities managers, operations
            directors, and property managers across a wide range of commercial
            environments. Fill out the form and a member of our team will
            follow up within one business day.
          </p>

          <ul className="mt-10 space-y-4">
            {REASSURANCE.map((r, i) => (
              <li
                key={i}
                data-testid={`contact-reassurance-${i}`}
                className="flex items-start gap-3 border-t border-[var(--xw-line-soft)] pt-4"
              >
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="mt-1 shrink-0"
                  style={{ color: "var(--xw-orange)" }}
                />
                <span className="text-[14px] text-[var(--xw-ink-soft)] leading-snug">
                  {r}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right form */}
        <div className="col-span-12 lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-[var(--xw-line)] p-5 sm:p-6 md:p-10"
          >
            <div className="flex items-center justify-between border-b border-[var(--xw-line)] pb-4 mb-8">
              <span className="font-mono-xw text-[11px] tracking-[0.22em] uppercase">
                Discovery Call / Form 001
              </span>
              <span className="font-mono-xw text-[11px] tracking-[0.2em] text-[var(--xw-muted)]">
                {done ? "STATUS: SUBMITTED" : "STATUS: DRAFT"}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="xw-label">First Name *</label>
                <input
                  required
                  data-testid="contact-input-first-name"
                  className="xw-input"
                  placeholder="Jane"
                  value={form.first_name}
                  onChange={update("first_name")}
                />
              </div>
              <div>
                <label className="xw-label">Last Name *</label>
                <input
                  required
                  data-testid="contact-input-last-name"
                  className="xw-input"
                  placeholder="Smith"
                  value={form.last_name}
                  onChange={update("last_name")}
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="xw-label">Business or Organization *</label>
              <input
                required
                data-testid="contact-input-business"
                className="xw-input"
                placeholder="Smith's Market"
                value={form.business}
                onChange={update("business")}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="xw-label">Your Role *</label>
                <select
                  required
                  data-testid="contact-select-role"
                  className="xw-select"
                  value={form.role}
                  onChange={update("role")}
                >
                  <option value="">Select role</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="xw-label">Property Type *</label>
                <select
                  required
                  data-testid="contact-select-property"
                  className="xw-select"
                  value={form.property_type}
                  onChange={update("property_type")}
                >
                  <option value="">Select property</option>
                  {PROPERTIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="xw-label">Email Address *</label>
                <input
                  required
                  type="email"
                  data-testid="contact-input-email"
                  className="xw-input"
                  placeholder="jane@smithsmarket.com"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>
              <div>
                <label className="xw-label">Phone (optional)</label>
                <input
                  type="tel"
                  data-testid="contact-input-phone"
                  className="xw-input"
                  placeholder="(216) 555-0100"
                  value={form.phone}
                  onChange={update("phone")}
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="xw-label">Tell us about your property</label>
              <textarea
                data-testid="contact-textarea-message"
                className="xw-textarea"
                placeholder="Approximate cart count, number of locations, current challenges..."
                value={form.message}
                onChange={update("message")}
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-[12px] text-[var(--xw-muted)] max-w-[42ch]">
                We respond within one business day. Your information is never
                shared with third parties.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="btn-solid disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Submitting…" : "Submit Request"}
                <ArrowRight size={16} />
              </button>
            </div>

            {done && (
              <div
                data-testid="contact-success-message"
                className="mt-8 border border-[var(--xw-orange)] bg-[var(--xw-orange)]/10 p-4 flex items-start gap-3"
              >
                <CheckCircle2
                  size={18}
                  className="mt-0.5"
                  style={{ color: "var(--xw-orange)" }}
                />
                <div>
                  <div className="font-display font-bold text-[15px]">
                    Request received.
                  </div>
                  <div className="text-[13.5px] text-[var(--xw-ink-soft)]">
                    A member of our team will follow up within one business day.
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
